import type { IModel, Position, Size } from '../types';
import type { IUIModel } from '../../../stores/model/uimodel';
import type { ILink } from '../../../stores/model/uimodel/link';
import type { IFSMView } from '../../../stores/model/uimodel/fsm';
import type { IFSMLink } from '../../../stores/model/uimodel/fsmlink';
import type { IObjectType } from '../../../stores/model/metamodel/object_type';
import type { IDependency } from '../../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../../stores/model/metamodel/inheritance';
import type { IEventType } from '../../../stores/model/metamodel/event_type';
import type { IFSM } from '../../../stores/model/metamodel/fsm';
import type { IState } from '../../../stores/model/metamodel/state';
import type { ITransition } from '../../../stores/model/metamodel/transition';

// Vertices are the editable mid-points of an EDG/FSM link. Reuse the link's own
// snapshot vertex shape so the uimodel commands stay aligned with the store.
type Vertices = Parameters<ILink['setVertices']>[0];
// The FSM view's node/link creation snapshot shapes, reused verbatim.
type FSMNodePosition = Parameters<IFSMView['addNode']>[1];
type FSMNodeSize = Parameters<IFSMView['addNode']>[2];
type FSMLinkVertices = Parameters<IFSMView['addLink']>[1];
type FSMLinkSetVertices = Parameters<IFSMLink['setVertices']>[0];
type FSMLabelPosition = Parameters<IFSMLink['setLabelPosition']>[0];

// Find the EDG node for a given object type or create it if it doesn't exist
// yet.
function findOrCreateNode(uimodel: IUIModel, objectType: IObjectType) {
  let node = uimodel.edg.findNode(objectType);
  if (!node) {
    node = createEDGNode(uimodel, objectType);
  }
  return node;
}

// Create an EDG node for the given objectType at the given position
export function createEDGNode(
  uimodel: IUIModel,
  objectType: IObjectType,
  position?: Position
) {
  const node = uimodel.edg.addNode(objectType.id, position);
  return node;
}

// Remove the EDG node for the given objectType
export function removeEDGNode(uimodel: IUIModel, objectType: IObjectType): void {
  uimodel.edg.removeNode(objectType.id);
}

// Update the position of the EDG node for the given object type
export function updateEDGPosition(
  uimodel: IUIModel,
  objectType: IObjectType,
  position: Position
): void {
  const node = findOrCreateNode(uimodel, objectType);
  node.updatePosition(position.x, position.y);
}

// Update the size of the EDG node for the given object type
export function updateEDGSize(uimodel: IUIModel, objectType: IObjectType, size: Size): void {
  const node = findOrCreateNode(uimodel, objectType);
  node.updateSize(size.width, size.height);
}

// Find the EDG link for a given dependency or create it if it doesn't exist
// yet.
function findOrCreateLink(uimodel: IUIModel, dependency: IDependency) {
  let link = uimodel.edg.findLink(dependency);
  if (!link) {
    link = createEDGLink(uimodel, dependency);
  }
  return link;
}

// Create an EDG link with the given vertices for the given dependency
export function createEDGLink(
  uimodel: IUIModel,
  dependency: IDependency,
  vertices?: Vertices
) {
  const link = uimodel.edg.addLink(dependency.id, vertices);
  return link;
}

// Remove the EDG link for the given dependency
export function removeEDGLink(uimodel: IUIModel, dependency: IDependency): void {
  uimodel.edg.removeLink(dependency.id);
}

// Update the vertices of the EDG link for the given dependency
export function updateEDGVertices(
  uimodel: IUIModel,
  dependency: IDependency,
  vertices: Vertices
): void {
  const link = findOrCreateLink(uimodel, dependency);
  link.setVertices(vertices);
}

// Clear the vertices of all EDG links (needed before auto-layout)
export function clearAllEDGVertices(uimodel: IUIModel): void {
  uimodel.edg.clearAllVertices();
}

function findOrCreateInheritanceLink(uimodel: IUIModel, inheritance: IInheritance) {
  let link = uimodel.edg.findInheritanceLink(inheritance);
  if (!link) {
    link = createEDGInheritanceLink(uimodel, inheritance);
  }
  return link;
}

// Create an EDG link with the given vertices for the given inheritance
export function createEDGInheritanceLink(
  uimodel: IUIModel,
  inheritance: IInheritance,
  vertices?: Vertices
) {
  const link = uimodel.edg.addInheritanceLink(inheritance.id, vertices);
  return link;
}

// Remove the EDG link for the given inheritance
export function removeEDGInheritanceLink(uimodel: IUIModel, inheritance: IInheritance): void {
  uimodel.edg.removeInheritanceLink(inheritance.id);
}

// Update the vertices of the EDG link for the given dependency
export function updateEDGInheritanceVertices(
  uimodel: IUIModel,
  dependency: IInheritance,
  vertices: Vertices
): void {
  const link = findOrCreateInheritanceLink(uimodel, dependency);
  link.setVertices(vertices);
}

// Initialize the OET ui model if it is empty
export function initializeOET(model: IModel): boolean {
  if (model.uimodel.oet.empty) {
    model.metamodel.eventTypes.forEach((et) => appendOETRow(model.uimodel, et));
    model.metamodel.objectTypes.forEach((ot) => appendOETColumn(model.uimodel, ot));
    return true;
  } else {
    return false;
  }
}

// Append a column to the OET for the given object type
export function appendOETColumn(uimodel: IUIModel, objectType: IObjectType): void {
  uimodel.oet.addColumn(objectType.id);
}

// Append a row to the OET for the given event type
export function appendOETRow(uimodel: IUIModel, eventType: IEventType): void {
  if (eventType.specialised) {
    uimodel.oet.addSubRow(eventType.ownerEventType!.id, eventType.id);
  } else {
    uimodel.oet.addRow(eventType.id);
  }
}

// Remove the OET column for the given object type
export function removeOETColumn(uimodel: IUIModel, objectType: IObjectType): void {
  uimodel.oet.removeColumn(objectType.id);
}

// Remove the OET row for the given event type
export function removeOETRow(uimodel: IUIModel, eventType: IEventType): void {
  if (eventType.specialised) {
    uimodel.oet.removeSubRow(eventType.ownerEventType!.id, eventType.id);
  } else {
    uimodel.oet.removeRow(eventType.id);
  }
}

// Make the OET row for the given event a root row
export function makeOETRootRow(uimodel: IUIModel, eventType: IEventType): void {
  if (eventType.specialised) {
    uimodel.oet.moveRowToRoot(eventType.ownerEventType!.id, eventType.id);
  } else {
    // it should be a root row already
  }
}

// Make the OET row for the given event a subrow
export function makeOETSubRow(uimodel: IUIModel, eventType: IEventType): void {
  if (eventType.specialised) {
    uimodel.oet.moveRowToParent(eventType.ownerEventType!.id, eventType.id);
  }
}

// Create an FSM view for the given FSM
export function createFSMView(uimodel: IUIModel, fsm: IFSM) {
  return uimodel.addFSM(fsm.id);
}

// Create an FSM node for the given state
export function createFSMNode(
  fsmview: IFSMView,
  state: IState,
  position?: FSMNodePosition,
  size?: FSMNodeSize
) {
  return createFSMNodeByStateId(fsmview, state.id, position, size);
}

// Create an FSM node for the given state (id)
export function createFSMNodeByStateId(
  fsmview: IFSMView,
  stateId: string,
  position?: FSMNodePosition,
  size?: FSMNodeSize
) {
  return fsmview.addNode(stateId, position, size);
}

// Create an FSM link for the given transition and vertices
export function createFSMLink(
  fsmview: IFSMView,
  transition: ITransition,
  vertices: FSMLinkVertices = []
) {
  return createFSMLinkByTransitionId(fsmview, transition.id, vertices);
}

// Create an FSM link for the given transition (id) and vertices
export function createFSMLinkByTransitionId(
  fsmview: IFSMView,
  transitionId: string,
  vertices: FSMLinkVertices = []
) {
  return fsmview.addLink(transitionId, vertices);
}

// Remove the FSM view for the given fsm
export function removeFSMView(uimodel: IUIModel, fsm: IFSM): void {
  uimodel.removeFSM(fsm.id);
}

export function removeFSMNode(uimodel: IUIModel, fsm: IFSM, state: IState): void {
  const fsmview = uimodel.findFSM(fsm)!;
  fsmview.removeNode(state.id);
}

// Remove the FSM link for the given transition
export function removeFSMLink(uimodel: IUIModel, fsm: IFSM, transition: ITransition): void {
  const fsmview = uimodel.findFSM(fsm)!;
  fsmview.removeLink(transition.id);
}

// Update the position of the FSM node for the given state
export function updateFSMPosition(fsmview: IFSMView, state: IState, position: Position): void {
  const fsmnode = fsmview.findNodeById(state.id)!;
  fsmnode.updatePosition(position.x, position.y);
}

// Update the size of the FSM node for the given state
export function updateFSMSize(fsmview: IFSMView, state: IState, size: Size): void {
  const fsmnode = fsmview.findNodeById(state.id)!;
  fsmnode.updateSize(size.width, size.height);
}

// Update the vertices of the FSM link for the transition
export function updateFSMVertices(
  fsmview: IFSMView,
  transition: ITransition,
  vertices: FSMLinkSetVertices
): void {
  const link = fsmview.findLinkById(transition.id)!;
  link.setVertices(vertices);
}

// Update the label position of the FSM link for the transition
export function updateFSMLinkLabelPosition(
  fsmview: IFSMView,
  transition: ITransition,
  position: FSMLabelPosition
): void {
  const link = fsmview.findLinkById(transition.id)!;
  link.setLabelPosition(position);
}
